// services/examService.ts
import { db } from "../firebase/config";
import {
  doc,
  getDoc,
  collection,
  getDocs,
  query,
  where,
  addDoc,
  updateDoc,
  deleteDoc,
  DocumentData,
  QueryDocumentSnapshot
} from "firebase/firestore";
import {
  AssessmentTemplate,
  Section,
  Question,
  Trait,
  ExamAssignment,
  SectionTrait,
  Exam
} from "../types";

export interface ExamData {
  assessmentTemplate: AssessmentTemplate;
  assignment: ExamAssignment;
  candidateId: string;
  sections: Section[];
  questions: Question[];
  traits: Trait[];
}

interface TraitItem {
  traitId: string;
  traitName?: string;
  traitIcon?: string;
  scaleId?: string;
  scaleType?: string;
  weightage?: number;
  order?: number;
}

class ExamService {
  async fetchExamData(assignmentId: string): Promise<ExamData | null> {
    try {
      console.log("Fetching exam data for assignment:", assignmentId);

      const assignment = await this.fetchAssignment(assignmentId);
      if (!assignment) {
        console.error("Assignment not found");
        return null;
      }

      const assessmentTemplate = await this.fetchAssessmentTemplate(assignment.examId);
      if (!assessmentTemplate) {
        console.error("Assessment template not found");
        return null;
      }

      const { sections, questions } = await this.fetchSectionsAndQuestions(assessmentTemplate);
      const traits = await this.fetchTraitsByIds(sections);

      console.log("Exam data loaded:", {
        sectionsCount: sections.length,
        questionsCount: questions.length,
        traitsCount: traits.length
      });

      return {
        assessmentTemplate,
        assignment,
        candidateId: assignment.candidateId,
        sections,
        questions,
        traits
      };
    } catch (error) {
      console.error("Error fetching exam data:", error);
      return null;
    }
  }

  private async fetchAssignment(assignmentId: string): Promise<ExamAssignment | null> {
    try {
      const assignmentRef = doc(db, "exam_assignments", assignmentId);
      const assignmentSnapshot = await getDoc(assignmentRef);

      if (!assignmentSnapshot.exists()) {
        return null;
      }

      const data = assignmentSnapshot.data();
      return {
        id: assignmentSnapshot.id,
        examId: data.examId,
        candidateId: data.candidateId,
        orgId: data.orgId,
        status: data.status,
        assignedAt: data.assignedAt,
        scheduledStart: data.scheduledStart,
        scheduledEnd: data.scheduledEnd,
        startedAt: data.startedAt,
        completedAt: data.completedAt,
        score: data.score,
        maxScore: data.maxScore,
        timeSpent: data.timeSpent,
        createdBy: data.createdBy,
        notes: data.notes,
      };
    } catch (error) {
      console.error("Error fetching assignment:", error);
      return null;
    }
  }

  private async fetchAssessmentTemplate(templateId: string): Promise<AssessmentTemplate | null> {
    try {
      const templateRef = doc(db, "assessmentTemplates", templateId);
      const templateSnapshot = await getDoc(templateRef);

      if (!templateSnapshot.exists()) {
        return null;
      }

      const data = templateSnapshot.data();
      return {
        id: templateSnapshot.id,
        name: data.name,
        description: data.description,
        targetAudience: data.targetAudience || "all",
        sections: data.sections || [],
        totalQuestions: data.totalQuestions || 0,
        totalDuration: data.totalDuration || 0,
        instructions: data.instructions || "",
        timeLimit: data.timeLimit || 60,
        isActive: data.isActive || true,
        createdBy: data.createdBy || "",
        createdAt: data.createdAt || new Date().toISOString(),
        updatedAt: data.updatedAt || new Date().toISOString()
      };
    } catch (error) {
      console.error("Error fetching assessment template:", error);
      return null;
    }
  }

  private async fetchSectionsAndQuestions(template: AssessmentTemplate): Promise<{ sections: Section[], questions: Question[] }> {
    const sections: Section[] = [];
    const questions: Question[] = [];

    for (const sectionInfo of template.sections) {
      try {
        const sectionRef = doc(db, "sections", sectionInfo.sectionId);
        const sectionSnapshot = await getDoc(sectionRef);

        if (sectionSnapshot.exists()) {
          const sectionData = sectionSnapshot.data();

          const sectionTraits: SectionTrait[] = [];
          if (sectionInfo.traits && Array.isArray(sectionInfo.traits)) {
            for (const traitItem of sectionInfo.traits) {
              if (typeof traitItem === 'string') {
                const traitDoc = await getDoc(doc(db, "traits", traitItem));
                if (traitDoc.exists()) {
                  const traitData = traitDoc.data();
                  sectionTraits.push({
                    traitId: traitItem,
                    traitName: traitData.name,
                    traitIcon: traitData.icon || "📊",
                    scaleId: traitData.scaleId || "",
                    scaleType: traitData.scaleType || "likert_5",
                    weightage: traitData.weightage || 1,
                    order: 0
                  });
                }
              } else {
                const item = traitItem as TraitItem;
                if (item && item.traitId) {
                  sectionTraits.push({
                    traitId: item.traitId,
                    traitName: item.traitName || "",
                    traitIcon: item.traitIcon || "📊",
                    scaleId: item.scaleId || "",
                    scaleType: item.scaleType || "likert_5",
                    weightage: item.weightage || 1,
                    order: item.order || 0
                  });
                }
              }
            }
          }

          const section: Section = {
            id: sectionSnapshot.id,
            name: sectionData.name,
            description: sectionData.description || "",
            instructions: sectionData.instructions || sectionInfo.sectionName || "",
            order: sectionInfo.order || sectionData.order || 0,
            traits: sectionTraits,
            scaleType: sectionData.scaleType || "likert_5",
            // New fields
            totalQuestions: sectionInfo.numberOfQuestions || sectionData.totalQuestions || sectionData.questionCount || 15,
            minQuestions: sectionData.minQuestions || 10,
            selectionType: sectionData.selectionType || "random",
            timeLimit: sectionData.timeLimit || sectionData.duration || 15,
            autoSubmit: sectionData.autoSubmit !== undefined ? sectionData.autoSubmit : true,
            weightage: sectionData.weightage || 100,
            marksPerQuestion: sectionData.marksPerQuestion || 0,
            negativeMarking: sectionData.negativeMarking || false,
            isOptional: sectionData.isOptional || false,
            passingScore: sectionData.passingScore || 0,
            sectionType: sectionData.sectionType || "psychometric",
            allowedQuestionTypes: sectionData.allowedQuestionTypes || [],
            // Legacy fields for backward compatibility
            questionCount: sectionInfo.numberOfQuestions || sectionData.totalQuestions || sectionData.questionCount || 15,
            duration: sectionData.timeLimit || sectionData.duration || 15,
            createdAt: sectionData.createdAt || new Date().toISOString(),
            updatedAt: sectionData.updatedAt || new Date().toISOString()
          };
          sections.push(section);

          const questionBanks = await this.fetchQuestionBanksForSection(section.id);
          for (const bank of questionBanks) {
            const bankQuestions = await this.fetchQuestionsFromBank(bank.id, section.id);
            questions.push(...bankQuestions);
          }
        } else {
          console.warn(`Section not found: ${sectionInfo.sectionId}`);
        }
      } catch (error) {
        console.error(`Error fetching section ${sectionInfo.sectionId}:`, error);
      }
    }

    return { sections: sections.sort((a, b) => a.order - b.order), questions };
  }

  private async fetchQuestionBanksForSection(sectionId: string): Promise<any[]> {
    try {
      const banksRef = collection(db, "questionBanks");
      const q = query(banksRef, where("sectionId", "==", sectionId));
      const banksSnapshot = await getDocs(q);

      return banksSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error(`Error fetching question banks for section ${sectionId}:`, error);
      return [];
    }
  }

  private async fetchQuestionsFromBank(bankId: string, sectionId: string): Promise<Question[]> {
    try {
      const questionsRef = collection(db, "questions");
      const q = query(questionsRef, where("bankId", "==", bankId));
      const questionsSnapshot = await getDocs(q);

      const questions: Question[] = [];
      questionsSnapshot.forEach((docSnapshot: QueryDocumentSnapshot<DocumentData>) => {
        const data = docSnapshot.data();
        questions.push({
          id: docSnapshot.id,
          bankId: data.bankId,
          text: data.text,
          type: data.type || data.questionTypeCode || "likert",
          category: data.category || "behavioral",
          sectionId: sectionId,
          traitId: data.traitId,
          traitName: data.traitName,
          scaleId: data.scaleId || "",
          scaleType: data.scaleType || "likert_5",
          weightage: data.weightage || 1,
          isReverse: data.isReverse || false,
          maxScale: data.maxScale || 5,
          options: data.options || [],
          difficulty: data.difficulty || "medium",
          language: data.language || "en",
          scoringLogic: data.scoringLogic || "sum",
          status: data.status || "active",
          points: data.points || 1,
          timeLimit: data.timeLimit || null,
          createdBy: data.createdBy || "",
          createdAt: data.createdAt || new Date().toISOString(),
          updatedAt: data.updatedAt || new Date().toISOString(),
          order: data.order || 0
        });
      });

      console.log(`Loaded ${questions.length} questions from bank ${bankId} for section ${sectionId}`);
      return questions;
    } catch (error) {
      console.error(`Error fetching questions from bank ${bankId}:`, error);
      return [];
    }
  }

  private async fetchTraitsByIds(sections: Section[]): Promise<Trait[]> {
    const traitIds: string[] = [];
    for (const section of sections) {
      if (section.traits && Array.isArray(section.traits)) {
        for (const trait of section.traits) {
          if (!traitIds.includes(trait.traitId)) {
            traitIds.push(trait.traitId);
          }
        }
      }
    }

    const traits: Trait[] = [];
    for (let i = 0; i < traitIds.length; i++) {
      const traitId = traitIds[i];
      try {
        const traitRef = doc(db, "traits", traitId);
        const traitSnapshot = await getDoc(traitRef);

        if (traitSnapshot.exists()) {
          const data = traitSnapshot.data();
          traits.push({
            id: traitSnapshot.id,
            name: data.name,
            categoryId: data.categoryId || "",
            categoryName: data.categoryName,
            scaleType: data.scaleType || "likert_5",
            minScore: data.minScore || 0,
            maxScore: data.maxScore || 0,
            lowRangeMin: data.lowRangeMin || 0,
            lowRangeMax: data.lowRangeMax || 0,
            mediumRangeMin: data.mediumRangeMin || 0,
            mediumRangeMax: data.mediumRangeMax || 0,
            highRangeMin: data.highRangeMin || 0,
            highRangeMax: data.highRangeMax || 0,
            interpretationRules: data.interpretationRules || {
              low: "",
              medium: "",
              high: ""
            },
            weightage: data.weightage || 0,
            createdAt: data.createdAt || new Date().toISOString(),
            updatedAt: data.updatedAt || new Date().toISOString()
          });
        }
      } catch (error) {
        console.error(`Error fetching trait ${traitId}:`, error);
      }
    }

    return traits;
  }
}

// Export the service as default
export default new ExamService();

// ============================================================================
// Export functions for other pages
// ============================================================================

export async function getExams(orgId?: string): Promise<Exam[]> {
  try {
    const examsRef = collection(db, "assessmentTemplates");
    let q = query(examsRef);

    if (orgId) {
      q = query(examsRef, where("organizationId", "==", orgId));
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        orgId: data.organizationId || data.orgId || "",
        title: data.name,
        description: data.description || "",
        type: data.type || "behavioral",
        status: data.isActive ? "active" : "draft",
        duration: data.timeLimit || 60,
        sections: data.sections?.map((s: any) => s.sectionId) || [],
        totalQuestions: data.totalQuestions || 0,
        totalWeightage: data.totalWeightage || 100,
        negativeMarking: data.negativeMarking || false,
        negativeValue: data.negativeValue || 0,
        randomOrder: data.randomOrder || false,
        createdAt: data.createdAt || new Date().toISOString(),
        assignedCount: data.assignedCount || 0
      };
    });
  } catch (error) {
    console.error("Error fetching exams:", error);
    return [];
  }
}

export async function getExamById(examId: string): Promise<any | null> {
  try {
    const examRef = doc(db, "assessmentTemplates", examId);
    const snapshot = await getDoc(examRef);

    if (snapshot.exists()) {
      return { id: snapshot.id, ...snapshot.data() };
    }
    return null;
  } catch (error) {
    console.error("Error fetching exam:", error);
    return null;
  }
}

export async function createExam(examData: any): Promise<any> {
  try {
    const examsRef = collection(db, "assessmentTemplates");
    const docRef = await addDoc(examsRef, {
      ...examData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    return { id: docRef.id, ...examData };
  } catch (error) {
    console.error("Error creating exam:", error);
    throw error;
  }
}

export async function updateExam(examId: string, examData: any): Promise<any> {
  try {
    const examRef = doc(db, "assessmentTemplates", examId);
    await updateDoc(examRef, {
      ...examData,
      updatedAt: new Date().toISOString()
    });
    return { id: examId, ...examData };
  } catch (error) {
    console.error("Error updating exam:", error);
    throw error;
  }
}

export async function deleteExam(examId: string): Promise<boolean> {
  try {
    const examRef = doc(db, "assessmentTemplates", examId);
    await deleteDoc(examRef);
    return true;
  } catch (error) {
    console.error("Error deleting exam:", error);
    throw error;
  }
}